import { useAuthStore } from "@/stores/auth";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableEmptyRow,
  DataTableHead,
  DataTableHeadCell,
  DataTableHeaderRow,
  DataTableRow,
  DataTableScroll,
  DataTableShell,
} from "@/components/ui/DataTable";
import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { Clock3 } from "lucide-react";
import {
  useClubAccessRequests,
  useClubLiveStatus,
  useClubOverview,
  useClubSeasons,
} from "../hooks/useAdminData";
import type { ClubOverviewPayload, ClubOverviewRollup } from "@/lib/types";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";

/**
 * `/admin/overview` — the club console home (admin-console-split PR 5).
 *
 * A club-operations dashboard: usage, team momentum, and intervention
 * points, fed by the club-scoped `GET /api/club/overview`. Platform
 * telemetry (disk, encoder, backfill) lives on the super-admin Operations
 * page — a club admin never sees infrastructure here.
 */
/** Soft-limit banner: renders ONLY when a count exceeds its operator-set
 * limit (>100%, never a nag below it). Storage has no banner here — it is
 * hard-enforced at upload time with its own uploader-facing error. */
function QuotaWarning({ quota }: { quota?: ClubOverviewPayload["quota"] }) {
  if (!quota) return null;
  const over: string[] = [];
  if (quota.max_teams !== null && quota.team_count > quota.max_teams) {
    over.push(`teams (${quota.team_count}/${quota.max_teams})`);
  }
  if (quota.max_members !== null && quota.member_count > quota.max_members) {
    over.push(`members (${quota.member_count}/${quota.max_members})`);
  }
  if (over.length === 0) return null;
  return (
    <p
      data-testid="admin-overview-quota-warning"
      className="rounded border border-flag/40 bg-flag/10 px-3 py-2 text-xs text-ink"
    >
      Your club is over its {over.join(" and ")} limit. Contact the platform
      operator to raise it.
    </p>
  );
}

export function OverviewPage() {
  const me = useAuthStore((s) => s.me);
  const diag = useClubOverview();
  const canReadClub = !!(
    me?.is_club_admin ||
    me?.is_super_admin ||
    me?.user?.is_global_admin
  );
  const requests = useClubAccessRequests(canReadClub);
  const seasons = useClubSeasons(canReadClub);
  const defaultSeason = seasons.data?.find((season) => season.is_default);
  const live = useClubLiveStatus({ enabled: canReadClub });
  const pendingRequests = requests.data?.filter(
    (row) => row.status === "pending",
  ).length;
  const onAir =
    live.data && (!live.data.enabled || live.data.reachable)
      ? live.data.teams.filter((team) => team.ready).length
      : undefined;
  const d = diag.data;
  const club = d?.club;
  const totals = club?.totals;
  const status = deriveClubStatus(d);

  return (
    <div data-testid="admin-overview" className="flex flex-col gap-4">
      <AdminPageHeader
        eyebrow="Club administration"
        title={me?.current_club?.name || "Club Overview"}
        description="Your squads, people and matchday operations."
        actions={
          <Link to="/admin/settings" className="admin-overview-profile-link">
            Profile &amp; Brand
          </Link>
        }
      />

      {diag.error ? (
        <p
          data-testid="admin-overview-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load the club overview.
        </p>
      ) : null}

      <QuotaWarning quota={d?.quota} />

      {d &&
      (status === "critical" ||
        status === "attention" ||
        d.club.attention.length > 0) ? (
        <div className="admin-overview-alerts" role="status">
          <StatusBand
            payload={d}
            status={status === "healthy" ? "attention" : status}
          />
          <NeedsAttention payload={d} />
        </div>
      ) : null}

      <div
        data-testid="admin-overview-kpis"
        className="grid grid-cols-2 gap-5 lg:grid-cols-4"
      >
        <MetricTile
          testid="kpi-teams"
          label="Squads"
          value={totals ? String(totals.teams) : "—"}
          sub={
            totals
              ? `${totals.public_teams} public · Across the club`
              : "Loading squads…"
          }
        />
        <MetricTile
          testid="kpi-access-requests"
          label="Access requests"
          value={pendingRequests ?? "—"}
          sub={
            requests.isError
              ? "Requests unavailable"
              : pendingRequests === undefined
                ? "Loading requests…"
                : pendingRequests
                  ? "Ready for your decision"
                  : "No pending requests"
          }
        />
        <MetricTile
          testid="kpi-recordings"
          label="Recordings"
          value={
            d
              ? d.counts.ready_slots +
                d.counts.transcoding_slots +
                d.counts.failed_slots
              : "—"
          }
          sub={
            d ? `${d.counts.ready_slots} ready to watch` : "Loading recordings…"
          }
        />
        <MetricTile
          testid="kpi-on-air"
          label="On air"
          value={onAir ?? "—"}
          sub={
            live.isError || (live.data?.enabled && !live.data.reachable)
              ? "Live status unavailable"
              : "Team broadcasts"
          }
        />
      </div>

      <div className="admin-club-home">
        <section className="admin-club-setup" data-testid="admin-club-setup">
          <header className="admin-club-setup-heading">
            <div>
              <p className="eyebrow">Club setup</p>
              <h3>Ready for the season</h3>
            </div>
            {defaultSeason?.name ? (
              <span className="admin-default-season">
                {defaultSeason?.name}
              </span>
            ) : null}
          </header>
          {[
            [
              "Club identity",
              me?.current_club?.name || "Club profile and colours",
              "/admin/settings",
              "Edit profile",
            ],
            [
              "Squads & memberships",
              totals
                ? `${totals.teams} squads · ${totals.member_accounts} members`
                : "Loading squads…",
              "/admin/teams",
              "Manage squads",
            ],
            [
              "Access & consent",
              pendingRequests === undefined
                ? "Memberships, invitations and consent"
                : `${pendingRequests} pending requests · Memberships and consent`,
              "/admin/people",
              "Review people",
            ],
            [
              "Season calendar",
              defaultSeason?.name || "Manage your seasons",
              "/admin/seasons",
              "Open seasons",
            ],
          ].map(([title, detail, to, action]) => (
            <div className="admin-setup-row" key={to}>
              <div>
                <strong>{title}</strong>
                <p>{detail}</p>
              </div>
              <Link to={to}>{action}</Link>
            </div>
          ))}
        </section>
        <section
          className="admin-recent-activity"
          data-testid="admin-overview-recent-activity"
        >
          <p className="eyebrow">Recent activity</p>
          <h3>At the club</h3>
          {d?.recent_activity.length ? (
            <ul>
              {d.recent_activity.slice(0, 3).map((event, index) => (
                <li key={`${event.created_at}-${event.event_type}-${index}`}>
                  <Clock3 aria-hidden size={18} />
                  <div>
                    <p>
                      {event.message || event.event_type.replaceAll("_", " ")}
                    </p>
                    <time dateTime={event.created_at}>
                      {formatDate(event.created_at)}
                    </time>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="admin-activity-empty">
              {d ? "No recent club activity." : "Loading activity…"}
            </p>
          )}
        </section>
      </div>
      <section
        className="admin-activity-summary"
        data-testid="admin-overview-activity-summary"
      >
        <p className="eyebrow">Club activity</p>
        <h3>Across your teams</h3>
        <div className="admin-activity-metrics">
          <MetricTile
            testid="kpi-matches"
            label="Matches in the last 30 days"
            value={totals ? String(totals.matches_last_30d) : "-"}
            sub={totals ? `${totals.matches} total library matches` : ""}
          />
          <MetricTile
            testid="kpi-feedback"
            label="Feedback items in the last 30 days"
            value={totals ? String(totals.feedback_last_30d) : "-"}
            sub={totals ? `${totals.feedback_items} notes, clips, recaps` : ""}
          />
          <MetricTile
            testid="kpi-account-coverage"
            label="Active players with a linked account"
            value={totals ? `${totals.linked_player_pct}%` : "-"}
            sub={
              totals
                ? `${totals.linked_players} of ${totals.active_players} active players linked`
                : ""
            }
            tone={coverageTone(totals?.linked_player_pct)}
          />
        </div>
        <TeamMomentum teams={club?.team_metrics ?? []} />
      </section>
    </div>
  );
}

type Tone = "good" | "warn" | "bad" | "busy" | "idle" | "muted";
type ClubStatus = "healthy" | "attention" | "critical" | "loading";

const TONE_CLASSES: Record<Tone, string> = {
  good: "text-good",
  warn: "text-flag",
  bad: "text-destructive",
  busy: "text-accent",
  idle: "text-ink-muted",
  muted: "text-ink",
};

export function deriveClubStatus(
  payload: ClubOverviewPayload | undefined,
): ClubStatus {
  if (!payload) return "loading";
  const attention = payload.club?.attention ?? [];
  if (
    attention.some((item) => item.severity === "critical") ||
    (payload.counts.failed_slots ?? 0) > 0
  ) {
    return "critical";
  }
  if (
    attention.some((item) => item.severity === "warn") ||
    (payload.counts.hls_missing_slots ?? 0) > 0
  ) {
    return "attention";
  }
  return "healthy";
}

function StatusBand({
  payload,
  status,
}: {
  payload: ClubOverviewPayload | undefined;
  status: ClubStatus;
}): ReactNode {
  const copy: Record<
    ClubStatus,
    { label: string; tone: Tone; detail: string }
  > = {
    loading: {
      label: "Loading",
      tone: "muted",
      detail: "Reading the current club and system state.",
    },
    healthy: {
      label: "Healthy",
      tone: "good",
      detail: "No club or video pipeline blockers need attention.",
    },
    attention: {
      label: "Needs attention",
      tone: "warn",
      detail: "Usage or media health has items worth checking.",
    },
    critical: {
      label: "Critical",
      tone: "bad",
      detail: "A video pipeline issue may block your teams.",
    },
  };
  const item = copy[status];
  const attentionCount = payload?.club?.attention.length ?? 0;
  return (
    <section
      data-testid="admin-overview-status"
      data-status={status}
      className="rounded border border-rule bg-surface shadow-sm"
    >
      <div className="grid gap-3 p-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <StatusDot tone={item.tone} />
            <p className="font-display text-sm uppercase tracking-wider text-ink">
              {item.label}
            </p>
            <span className="rounded-full border border-rule px-2 py-0.5 font-mono text-[0.65rem] text-ink-muted">
              {attentionCount} action{attentionCount === 1 ? "" : "s"}
            </span>
          </div>
          <p className="mt-1 text-sm text-ink-muted">{item.detail}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4 md:flex md:items-center">
          <MiniSignal
            label="Failed"
            value={payload ? String(payload.counts.failed_slots) : "-"}
            tone={(payload?.counts.failed_slots ?? 0) > 0 ? "bad" : "good"}
          />
          <MiniSignal
            label="Uploads"
            value={payload ? String(activeUploadCount(payload)) : "-"}
            tone={activeUploadCount(payload) > 0 ? "busy" : "idle"}
          />
          <MiniSignal
            label="HLS"
            value={payload ? String(payload.counts.hls_missing_slots) : "-"}
            tone={
              (payload?.counts.hls_missing_slots ?? 0) > 0 ? "warn" : "good"
            }
          />
        </div>
      </div>
    </section>
  );
}

function MetricTile({
  label,
  value,
  sub,
  tone = "muted",
  testid,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  testid: string;
}): ReactNode {
  return (
    <div
      data-testid={testid}
      data-tone={tone}
      className="rounded border border-rule bg-surface p-3 shadow-sm"
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <div
        className={`mt-2 font-mono text-2xl leading-none ${TONE_CLASSES[tone]}`}
      >
        {value}
      </div>
      {sub ? <p className="mt-2 text-xs text-ink-muted">{sub}</p> : null}
    </div>
  );
}

function TeamMomentum({
  teams,
}: {
  teams: ClubOverviewRollup["team_metrics"];
}): ReactNode {
  const clubId = useAuthStore((state) => state.me?.current_club?.id);
  const collection = useCollection(teams, {
    scope: `club-momentum:${clubId}`,
    text: (team) =>
      `${team.team_name} ${team.game_format ?? ""} ${team.last_match_label ?? ""}`,
    matches: (team, filter) =>
      filter === "attention"
        ? team.needs_attention > 0
        : team.needs_attention === 0,
  });
  return (
    <section data-testid="admin-overview-teams" className="flex flex-col gap-2">
      <SectionTitle
        title="Team momentum"
        detail="Which teams are building a match and feedback rhythm."
      />
      <CollectionToolbar
        collection={collection}
        label="team activity"
        filters={[
          { value: "", label: "All teams" },
          { value: "attention", label: "Needs attention" },
          { value: "healthy", label: "No alerts" },
        ]}
      />
      <DataTableShell className="hidden md:block">
        <DataTableScroll>
          <DataTable>
            <DataTableHead>
              <DataTableHeaderRow>
                <DataTableHeadCell>Team</DataTableHeadCell>
                <DataTableHeadCell align="right">Matches</DataTableHeadCell>
                <DataTableHeadCell align="right">Feedback</DataTableHeadCell>
                <DataTableHeadCell align="right">Linked</DataTableHeadCell>
                <DataTableHeadCell>Last Match</DataTableHeadCell>
                <DataTableHeadCell align="right">Attention</DataTableHeadCell>
              </DataTableHeaderRow>
            </DataTableHead>
            <DataTableBody>
              {collection.items.length === 0 ? (
                <DataTableEmptyRow colSpan={6}>
                  {teams.length
                    ? "No teams match those filters."
                    : "No teams have been created yet."}
                </DataTableEmptyRow>
              ) : (
                collection.items.map((team) => (
                  <DataTableRow key={team.team_id}>
                    <DataTableCell>
                      <div className="flex min-w-0 flex-col">
                        <span className="font-display text-sm text-ink">
                          {team.team_name}
                        </span>
                        <span className="font-mono text-[0.65rem] text-ink-muted">
                          {team.game_format ?? "-"}{" "}
                          {team.public ? "public" : "private"}
                        </span>
                      </div>
                    </DataTableCell>
                    <DataTableCell align="right">
                      <MetricPair
                        main={team.matches}
                        sub={`+${team.matches_last_30d}/30d`}
                      />
                    </DataTableCell>
                    <DataTableCell align="right">
                      {team.feedback_counts_suppressed ? (
                        <MetricPair main="—" sub="hidden" />
                      ) : (
                        <MetricPair
                          main={team.feedback_items}
                          sub={`+${team.feedback_last_30d}/30d`}
                        />
                      )}
                    </DataTableCell>
                    <DataTableCell align="right">
                      <MetricPair
                        main={`${team.linked_player_pct}%`}
                        sub={`${team.linked_players}/${team.active_players}`}
                        tone={coverageTone(team.linked_player_pct)}
                      />
                    </DataTableCell>
                    <DataTableCell>
                      <span className="block text-xs text-ink">
                        {formatDate(team.last_match_date)}
                      </span>
                      <span className="block max-w-48 truncate text-[0.65rem] text-ink-muted">
                        {team.last_match_label ?? "No match"}
                      </span>
                    </DataTableCell>
                    <DataTableCell align="right">
                      <span
                        className={`font-mono text-sm ${
                          team.needs_attention > 0 ? "text-flag" : "text-good"
                        }`}
                      >
                        {team.needs_attention}
                      </span>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </DataTableBody>
          </DataTable>
        </DataTableScroll>
      </DataTableShell>
      <div className="grid gap-2 md:hidden">
        {collection.items.length === 0 ? (
          <div className="rounded border border-rule bg-surface p-3 text-sm text-ink-muted shadow-sm">
            {teams.length
              ? "No teams match those filters."
              : "No teams have been created yet."}
          </div>
        ) : (
          collection.items.map((team) => (
            <article
              key={team.team_id}
              className="rounded border border-rule bg-surface p-3 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate font-display text-sm text-ink">
                    {team.team_name}
                  </h3>
                  <p className="font-mono text-[0.65rem] text-ink-muted">
                    {team.game_format ?? "-"} ·{" "}
                    {team.public ? "public" : "private"}
                  </p>
                </div>
                <span
                  className={`font-mono text-sm ${
                    team.needs_attention > 0 ? "text-flag" : "text-good"
                  }`}
                >
                  {team.needs_attention} flags
                </span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <MobileMetric
                  label="Matches"
                  value={team.matches}
                  sub={`+${team.matches_last_30d}/30d`}
                />
                {team.feedback_counts_suppressed ? (
                  <MobileMetric label="Feedback" value="—" sub="hidden" />
                ) : (
                  <MobileMetric
                    label="Feedback"
                    value={team.feedback_items}
                    sub={`+${team.feedback_last_30d}/30d`}
                  />
                )}
                <MobileMetric
                  label="Linked"
                  value={`${team.linked_player_pct}%`}
                  sub={`${team.linked_players}/${team.active_players}`}
                />
              </div>
              <p className="mt-3 truncate text-xs text-ink-muted">
                Last: {formatDate(team.last_match_date)} ·{" "}
                {team.last_match_label ?? "No match"}
              </p>
            </article>
          ))
        )}
      </div>
      <CollectionPagination collection={collection} label="teams" />
    </section>
  );
}

function NeedsAttention({
  payload,
}: {
  payload: ClubOverviewPayload | undefined;
}): ReactNode {
  const items = payload?.club?.attention ?? [];
  const clubId = useAuthStore((state) => state.me?.current_club?.id);
  const collection = useCollection(items, {
    scope: `club-attention:${clubId}`,
    text: (item) => `${item.title} ${item.detail}`,
    matches: (item, filter) => item.severity === filter,
  });
  if (items.length === 0) return null;
  return (
    <section
      data-testid="admin-overview-attention"
      className="flex flex-col gap-2"
    >
      <SectionTitle
        title="Needs Attention"
        detail="Actionable club or media issues, not raw audit noise."
      />
      <CollectionToolbar
        collection={collection}
        label="alerts"
        filterLabel="Severity"
        filters={[
          { value: "", label: "All alerts" },
          { value: "critical", label: "Critical" },
          { value: "warn", label: "Warning" },
          { value: "info", label: "Information" },
        ]}
      />
      <div className="rounded border border-rule bg-surface shadow-sm">
        {collection.items.length === 0 ? (
          <p className="p-3 text-sm text-ink-muted">
            No alerts match those filters.
          </p>
        ) : (
          <ul className="divide-y divide-rule/60">
            {collection.items.map((item, index) => (
              <li
                key={`${item.kind}-${item.team_id ?? "club"}-${index}`}
                data-severity={item.severity}
                className="p-3"
              >
                <div className="flex items-start gap-2">
                  <StatusDot tone={attentionTone(item.severity)} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink">{item.title}</p>
                    <p className="mt-1 text-xs text-ink-muted">{item.detail}</p>
                    {item.href ? (
                      <Link
                        to={item.href}
                        className="mt-2 inline-flex rounded border border-rule px-2 py-1 font-display text-[0.6rem] uppercase tracking-wider text-ink-muted transition-colors hover:border-accent hover:text-accent"
                      >
                        Open
                      </Link>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <CollectionPagination collection={collection} label="alerts" />
    </section>
  );
}

function SectionTitle({
  title,
  detail,
}: {
  title: string;
  detail: string;
}): ReactNode {
  return (
    <header>
      <h3 className="font-display text-sm uppercase tracking-wider text-ink">
        {title}
      </h3>
      <p className="text-xs text-ink-muted">{detail}</p>
    </header>
  );
}

function MiniSignal({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone: Tone;
}): ReactNode {
  return (
    <div className="rounded border border-rule bg-elevated px-2 py-1.5">
      <span className="block text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className={`font-mono text-xs ${TONE_CLASSES[tone]}`}>{value}</span>
    </div>
  );
}

function MetricPair({
  main,
  sub,
  tone = "muted",
}: {
  main: ReactNode;
  sub: ReactNode;
  tone?: Tone;
}): ReactNode {
  return (
    <span className="inline-flex flex-col items-end">
      <span className={`font-mono text-sm ${TONE_CLASSES[tone]}`}>{main}</span>
      <span className="font-mono text-[0.65rem] text-ink-muted">{sub}</span>
    </span>
  );
}

function MobileMetric({
  label,
  value,
  sub,
}: {
  label: string;
  value: ReactNode;
  sub: ReactNode;
}): ReactNode {
  return (
    <div className="rounded border border-rule bg-elevated px-2 py-1.5">
      <span className="block text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className="block font-mono text-sm text-ink">{value}</span>
      <span className="block font-mono text-[0.65rem] text-ink-muted">
        {sub}
      </span>
    </div>
  );
}

function StatusDot({ tone }: { tone: Tone }): ReactNode {
  const classes: Record<Tone, string> = {
    good: "bg-good",
    warn: "bg-flag",
    bad: "bg-destructive",
    busy: "bg-accent",
    idle: "bg-ink-muted",
    muted: "bg-ink-muted",
  };
  return (
    <span
      aria-hidden="true"
      className={`mt-1 h-2 w-2 shrink-0 rounded-full ${classes[tone]}`}
    />
  );
}

function activeUploadCount(payload: ClubOverviewPayload | undefined): number {
  return (payload?.upload_sessions ?? []).filter(
    (session) => session.status === "active",
  ).length;
}

function coverageTone(value: number | undefined): Tone {
  if (value === undefined) return "muted";
  if (value >= 80) return "good";
  if (value >= 60) return "warn";
  return "bad";
}

function attentionTone(severity: "critical" | "warn" | "info"): Tone {
  if (severity === "critical") return "bad";
  if (severity === "warn") return "warn";
  return "muted";
}

/** Stable compact calendar date for persisted activity and match dates. */
function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.length > 10 ? value.slice(0, 10) : value;
  }
  return date.toISOString().slice(0, 10);
}
